import type { Household } from '../types/household'

/* Demonstration household — not a record of a real tenancy. */
export const household: Household = {
  id: 'hh-1',
  name: 'Cedarwood Flat 2B',
  stage: 'moving in',
  listingId: 'lst-1',
  listingName: 'Cedarwood Student Residence',
  area: 'Surulere, Lagos',
  rentPerYear: 700000,
  moveIn: '1 September 2026',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/801b0881-0c4e-493a-b137-edaeaeef5f49.jpg',
  members: [
    {
      id: 'me',
      name: 'Amarachi Obi',
      initials: 'AO',
      handle: '@amarachi',
      programme: 'B.Sc. Systems Engineering',
      role: 'Lead tenant',
      sharePerYear: 240000,
      joined: '12 June 2026',
    },
    {
      id: 'hm-2',
      name: 'Chidinma Eze',
      initials: 'CE',
      handle: '@chidinma',
      programme: 'B.Sc. Computer Science',
      role: 'Co-tenant',
      sharePerYear: 240000,
      joined: '14 June 2026',
      compatibility: 94,
    },
    {
      id: 'hm-3',
      name: 'David Okonkwo',
      initials: 'DO',
      handle: '@davidok',
      programme: 'B.Eng. Civil Engineering',
      role: 'Co-tenant',
      sharePerYear: 220000,
      joined: '19 June 2026',
      compatibility: 81,
    },
    {
      id: 'hm-4',
      name: 'Tobi Adeyemi',
      initials: 'TA',
      handle: '@tobia',
      programme: 'B.Sc. Economics',
      role: 'Invited',
      sharePerYear: 0,
      joined: 'Invitation sent 2 July 2026',
      compatibility: 76,
    },
  ],
  checklist: [
    {
      id: 'ck-1',
      label: 'Application approved',
      detail: 'The property manager accepted the household application for Flat 2B.',
      owner: 'Everyone',
      done: true,
    },
    {
      id: 'ck-2',
      label: 'Tenancy agreement signed',
      detail: 'All three named tenants signed. Tobi is not on the agreement.',
      owner: 'Everyone',
      done: true,
    },
    {
      id: 'ck-3',
      label: 'First payment made',
      detail:
        'Rent is paid to the property manager directly. Campuzon records that it happened; it does not hold the money.',
      owner: 'Amarachi Obi',
      done: true,
    },
    {
      id: 'ck-4',
      label: 'Keys collected',
      detail: 'Collected in person from the manager’s office. Two sets for three tenants.',
      owner: 'Everyone',
      done: false,
    },
    {
      id: 'ck-5',
      label: 'Room condition recorded',
      detail:
        'Photograph every room before you move anything in. This is your evidence if a deposit is disputed later.',
      owner: 'Everyone',
      done: false,
    },
    {
      id: 'ck-6',
      label: 'Electricity meter registered',
      detail: 'The manager provides the meter number on the day.',
      owner: 'David Okonkwo',
      done: false,
      blockedBy: 'Waiting on the property manager to confirm the meter number.',
    },
    {
      id: 'ck-7',
      label: 'Internet arranged',
      detail: 'The building has no included connection. Compare providers before you arrive.',
      owner: 'Chidinma Eze',
      done: false,
    },
    {
      id: 'ck-8',
      label: 'House rules agreed',
      detail: 'Everyone on the agreement has read and accepted the rules below.',
      owner: 'Everyone',
      done: false,
    },
  ],
  expenses: [
    {
      id: 'ex-1',
      label: 'Rent — first instalment',
      category: 'Rent',
      amount: 350000,
      paidBy: 'Amarachi Obi',
      when: '28 June 2026',
      between: ['me', 'hm-2', 'hm-3'],
      settled: false,
    },
    {
      id: 'ex-2',
      label: 'Cleaning before move-in',
      category: 'One-off',
      amount: 18000,
      paidBy: 'Chidinma Eze',
      when: '3 July 2026',
      between: ['me', 'hm-2', 'hm-3'],
      settled: true,
    },
    {
      id: 'ex-3',
      label: 'Kitchen essentials',
      category: 'Household',
      amount: 42500,
      paidBy: 'David Okonkwo',
      when: '5 July 2026',
      between: ['me', 'hm-2', 'hm-3'],
      settled: false,
    },
  ],
  tasks: [
    {
      id: 'tk-1',
      label: 'Kitchen and shared bathroom',
      assignee: 'Chidinma Eze',
      cadence: 'Weekly',
      due: 'Sunday',
      done: false,
    },
    {
      id: 'tk-2',
      label: 'Refill the water dispenser',
      assignee: 'David Okonkwo',
      cadence: 'Weekly',
      due: 'Wednesday',
      done: true,
    },
    {
      id: 'tk-3',
      label: 'Top up the electricity meter',
      assignee: 'Amarachi Obi',
      cadence: 'Monthly',
      due: '1st',
      done: false,
    },
  ],
  rules: [
    {
      id: 'rl-1',
      rule: 'Quiet from 10pm on weeknights',
      agreedBy: ['me', 'hm-2', 'hm-3'],
      note: 'All three of us said we study at home in the evenings.',
    },
    {
      id: 'rl-2',
      rule: 'Overnight guests: tell the group beforehand',
      agreedBy: ['me', 'hm-2', 'hm-3'],
    },
    {
      id: 'rl-3',
      rule: 'Shared shopping is split three ways and logged the same day',
      agreedBy: ['me', 'hm-2'],
      note: 'David has not confirmed this one yet.',
    },
  ],
  documents: [
    {
      id: 'dc-1',
      name: 'Cedarwood tenancy agreement.pdf',
      kind: 'Tenancy agreement',
      addedBy: 'Amarachi Obi',
      added: '26 June 2026',
      visibleTo: 'Household',
    },
    {
      id: 'dc-2',
      name: 'First instalment receipt.pdf',
      kind: 'Receipt',
      addedBy: 'Amarachi Obi',
      added: '28 June 2026',
      visibleTo: 'Household',
    },
    {
      id: 'dc-3',
      name: 'My identification.jpg',
      kind: 'Identification',
      addedBy: 'Amarachi Obi',
      added: '20 June 2026',
      visibleTo: 'Only me',
    },
  ],
}
